/**
 * Lead Command Center (LCC) — Supabase-native domain vocabulary.
 *
 * These constants define the LCC product's own pipeline. They are shared by
 * the LCC demo Worker (apps/worker/src/demo/lcc) and the web demo mode so the
 * two never drift. Snake_case values are what Supabase stores; the display
 * form shown in the UI is derived with lccStatusLabel()/lccStatusValue().
 *
 * Airtable does NOT define this shape — it is optional adapter territory.
 */

export const LCC_LEAD_STATUSES = [
  'new',
  'needs_review',
  'ready_for_verification',
  'verified',
  'qualified',
  'callback_needed',
  'waiting_on_client',
  'appointment_set',
  'estimate_sent',
  'converted',
  'lost',
  'recycle_later',
  'spam',
] as const;

export type LccLeadStatus = (typeof LCC_LEAD_STATUSES)[number];

export const LCC_LEAD_SOURCES = [
  'website_form',
  'phone_call',
  'google_ads',
  'facebook_ads',
  'referral',
  'email',
  'chat',
  'manual_entry',
  'imported',
] as const;

export type LccLeadSource = (typeof LCC_LEAD_SOURCES)[number];

/** snake_case → "Title Case" (deterministic, reversible via lccStatusValue). */
export function lccStatusLabel(value: string): string {
  return value
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** "Title Case" → snake_case. Accepts snake_case input unchanged. */
export function lccStatusValue(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, '_');
}

/** Display labels for every LCC lead status, in pipeline order. */
export const LCC_LEAD_STATUS_LABELS = LCC_LEAD_STATUSES.map(lccStatusLabel);

export function isLccLeadStatus(value: string): value is LccLeadStatus {
  return (LCC_LEAD_STATUSES as readonly string[]).includes(value);
}
